// src/modules/financials/pages/FinancialDashboardPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Layout, Typography, Row, Col, Space, Button, Radio, DatePicker, Select, Spin, Alert, Card, Divider,
} from 'antd';
import {
  ReloadOutlined,
  FilterOutlined,
  FundOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchFinancialDashboard } from '../api';
import { fetchBranches } from '../../dashboard/api';
import OverviewKPIs from '../components/OverviewKPIs';
import RevenueTrendChart from '../components/RevenueTrendChart';
import ProfitLossSection from '../components/ProfitLossSection';
import CapitalExposureSection from '../components/CapitalExposureSection';
import InventoryPipelineSection from '../components/InventoryPipelineSection';
import AdvanceAnalyticsSection from '../components/AdvanceAnalyticsSection';
import { usePermissions } from '../../../hooks/usePermissions';

const { Content } = Layout;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const PRESETS = [
  { label: 'Today', value: 'today' },
  { label: 'This Week', value: 'week' },
  { label: 'This Month', value: 'month' },
  { label: 'Quarter', value: 'quarter' },
  { label: 'Custom', value: 'custom' },
];

const FinancialDashboardPage = () => {
  const { canViewField } = usePermissions();

  const canViewOverview = canViewField('financials', 'overview');
  const canViewRevenueTrend = canViewField('financials', 'revenueTrend');
  const canViewProfitLoss = canViewField('financials', 'profitLoss');
  const canViewCapitalExposure = canViewField('financials', 'capitalExposure');
  const canViewInventory = canViewField('financials', 'inventory');
  const canViewAdvances = canViewField('financials', 'advances');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Filter state
  const [preset, setPreset] = useState('month');
  const [customRange, setCustomRange] = useState(null);
  const [branch, setBranch] = useState('all');
  const [branches, setBranches] = useState([]);

  // Load branches once
  useEffect(() => {
    fetchBranches()
      .then((res) => {
        if (res.success) setBranches(res.data);
      })
      .catch(() => {});
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = { branch };
      if (preset === 'custom' && customRange) {
        params.startDate = customRange[0].format('YYYY-MM-DD');
        params.endDate = customRange[1].format('YYYY-MM-DD');
      } else if (preset !== 'custom') {
        params.preset = preset;
      }

      const res = await fetchFinancialDashboard(params);
      if (res.success) {
        setData(res.data);
      } else {
        throw new Error(res.message || 'Failed to load financial data');
      }
    } catch (err) {
      console.error('[Financials] Fetch error:', err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [preset, customRange, branch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <Content style={{ padding: '24px', minHeight: 'calc(100vh - 64px)', background: '#f5f5f5' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
        <Col>
          <Space align="center">
            <FundOutlined style={{ fontSize: 24, color: '#1890ff' }} />
            <Title level={3} style={{ margin: 0 }}>Financial Dashboard</Title>
          </Space>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
            Refresh
          </Button>
        </Col>
      </Row>

      {/* Filters */}
      <Card
        style={{ borderRadius: 12, marginBottom: 20, border: '1px solid #f0f0f0' }}
        bodyStyle={{ padding: '12px 20px' }}
      >
        <Row gutter={[16, 12]} align="middle">
          <Col>
            <Space>
              <FilterOutlined style={{ color: '#1890ff' }} />
              <Text strong style={{ fontSize: 13 }}>Period:</Text>
            </Space>
          </Col>
          <Col>
            <Radio.Group
              value={preset}
              onChange={(e) => setPreset(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              size="small"
            >
              {PRESETS.map((p) => (
                <Radio.Button key={p.value} value={p.value}>
                  {p.label}
                </Radio.Button>
              ))}
            </Radio.Group>
          </Col>

          {preset === 'custom' && (
            <Col>
              <RangePicker
                size="small"
                value={customRange}
                onChange={setCustomRange}
                presets={[
                  { label: 'Last 7 Days', value: [dayjs().subtract(7, 'day'), dayjs()] },
                  { label: 'Last 30 Days', value: [dayjs().subtract(30, 'day'), dayjs()] },
                  { label: 'Last 90 Days', value: [dayjs().subtract(90, 'day'), dayjs()] },
                ]}
              />
            </Col>
          )}

          <Col>
            <Select
              value={branch}
              onChange={setBranch}
              size="small"
              style={{ minWidth: 160 }}
              placeholder="Branch"
            >
              <Select.Option value="all">All Branches</Select.Option>
              {branches.map((b) => (
                <Select.Option key={b._id} value={b._id}>
                  {b.name}
                </Select.Option>
              ))}
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Error */}
      {error && (
        <Alert
          message="Error loading financial data"
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 20, borderRadius: 8 }}
          onClose={() => setError(null)}
        />
      )}

      {/* Loading */}
      {loading && !data && (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <Spin size="large" tip="Loading financial data..." />
        </div>
      )}

      {/* Content */}
      {data && (
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
          {/* Row 1 — Overview KPIs */}
          {canViewOverview && <OverviewKPIs data={data.overview} />}

          {/* Row 2 — Revenue Trend */}
          {canViewRevenueTrend && <RevenueTrendChart data={data.revenueTrend} loading={loading} />}

          {/* Row 3 — Profit/Loss (full width) */}
          {canViewProfitLoss && (
            <ProfitLossSection data={data.profitLoss} loading={loading} />
          )}

          {/* Row 4 — Capital Exposure (full width) */}
          {canViewCapitalExposure && (
            <CapitalExposureSection data={data.capitalExposure} loading={loading} />
          )}

          {/* Row 4 — Inventory Pipeline */}
          {canViewInventory && <InventoryPipelineSection data={data.inventory} loading={loading} />}

          {/* Row 5 — Advance Analytics */}
          {canViewAdvances && <AdvanceAnalyticsSection data={data.advances} loading={loading} />}

          {/* Footer meta */}
          {data.meta && (
            <div style={{ textAlign: 'right', paddingTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Generated: {dayjs(data.meta.generatedAt).format('DD MMM YYYY, hh:mm A')}
                {data.meta.dateRange.startDate && (
                  <> &middot; Range: {dayjs(data.meta.dateRange.startDate).format('DD MMM')} – {dayjs(data.meta.dateRange.endDate).format('DD MMM YYYY')}</>
                )}
              </Text>
            </div>
          )}
        </Space>
      )}
    </Content>
  );
};

export default FinancialDashboardPage;
